// Working Capital Optimization Agent UI (Polished)
// Additions: CCC waterfall (DSO + DIO - DPO), DSO/DPO gauges, forecast trend,
// comparison vs industry benchmark, executive dashboard.
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Chip, Stack, Paper, Grid, Divider, FormControlLabel, Switch, Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, SimpleBarChart, RadialGauge, fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import { wcCcc, wcDso, wcDpo, wcInventory, wcForecast, wcExecutive } from './api';

const TABS = ['CCC Overview', 'DSO', 'DPO', 'Inventory (DIO)', 'Forecast', 'Executive Briefing'];

function dsoColor(n) { return n > 60 ? CHART_COLORS.danger : n > 45 ? CHART_COLORS.warning : CHART_COLORS.success; }
function dpoColor(n) { return n < 20 ? CHART_COLORS.warning : n > 60 ? CHART_COLORS.danger : CHART_COLORS.success; }

export default function WorkingCapitalAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [useAi, setUseAi] = useState(true);

    const [ccc, setCcc] = useState(null);
    const [dso, setDso] = useState(null);
    const [dpo, setDpo] = useState(null);
    const [inv, setInv] = useState(null);
    const [forecast, setForecast] = useState(null);
    const [exec, setExec] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const load = async () => {
            if (tab === 0 && !ccc) { const r = await run(() => wcCcc(365)); if (r?.data?.success) setCcc(r.data.data); }
            else if (tab === 1 && !dso) { const r = await run(() => wcDso()); if (r?.data?.success) setDso(r.data.data); }
            else if (tab === 2 && !dpo) { const r = await run(() => wcDpo()); if (r?.data?.success) setDpo(r.data.data); }
            else if (tab === 3 && !inv) { const r = await run(() => wcInventory()); if (r?.data?.success) setInv(r.data.data); }
            else if (tab === 4 && !forecast) { const r = await run(() => wcForecast(90)); if (r?.data?.success) setForecast(r.data.data); }
            else if (tab === 5 && !exec) { const r = await run(() => wcExecutive(useAi)); if (r?.data?.success) setExec(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const summaryCards = [
        { label: 'DSO', value: ccc?.dso_days != null ? `${ccc.dso_days}d` : '—', sublabel: 'Days Sales Outstanding', accent: 'info' },
        { label: 'DIO', value: ccc?.dio_days != null ? `${ccc.dio_days}d` : '—', sublabel: 'Days Inventory Outstanding', accent: 'warning' },
        { label: 'DPO', value: ccc?.dpo_days != null ? `${ccc.dpo_days}d` : '—', sublabel: 'Days Payable Outstanding', accent: 'success' },
        { label: 'CCC', value: ccc?.ccc_days != null ? `${ccc.ccc_days}d` : '—', sublabel: 'Cash Conversion Cycle', accent: ccc?.ccc_days > 60 ? 'danger' : 'primary' },
    ];

    // Waterfall data: DSO (+), DIO (+), DPO (-), CCC (net)
    const waterfallData = ccc ? [
        { component: 'DSO', days: ccc.dso_days || 0 },
        { component: 'DIO', days: ccc.dio_days || 0 },
        { component: 'DPO (-)', days: -(ccc.dpo_days || 0) },
        { component: 'CCC', days: ccc.ccc_days || 0 },
    ] : [];

    const renderTab = () => {
        switch (tab) {
            case 0:
                return ccc ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Cash Conversion Cycle" subtitle="DSO + DIO - DPO = CCC. Lower is better." />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={8}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" fontWeight={600}>CCC Decomposition (days)</Typography>
                                    <SimpleBarChart
                                        data={waterfallData}
                                        xKey="component"
                                        series={[{ key: 'days', label: 'Days', color: CHART_COLORS.primary }]}
                                        height={240}
                                        showLegend={false}
                                    />
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                    <Typography variant="caption" fontWeight={600}>CCC (Lower = Better)</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <RadialGauge
                                            value={ccc.ccc_days ?? 0}
                                            max={120}
                                            label={ccc.ccc_days > 60 ? 'High' : ccc.ccc_days > 30 ? 'Moderate' : 'Healthy'}
                                            color={ccc.ccc_days > 60 ? CHART_COLORS.danger : ccc.ccc_days > 30 ? CHART_COLORS.warning : CHART_COLORS.success}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                        <SummaryGrid cards={[
                            { label: 'AR Outstanding', value: fmtMoney(ccc.ar_outstanding), accent: 'info' },
                            { label: 'AP Outstanding', value: fmtMoney(ccc.ap_outstanding), accent: 'warning' },
                        ]} cols={2} />
                        {ccc.note && <Typography variant="caption" color="text.secondary">{ccc.note}</Typography>}
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 1:
                return dso ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="DSO Improvement Recommender" subtitle="Actionable strategies to reduce collection time" />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                    <Typography variant="caption" fontWeight={600}>Current DSO</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <RadialGauge
                                            value={dso.current_dso || 0}
                                            max={90}
                                            label={dso.current_dso > 60 ? 'High' : dso.current_dso > 45 ? 'Moderate' : 'Healthy'}
                                            color={dsoColor(dso.current_dso || 0)}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" fontWeight={600}>Cash Impact Per Day of DSO Reduction</Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 700, color: CHART_COLORS.success, mt: 1 }}>
                                        {fmtMoney(dso.cash_impact_per_day_reduction)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Reducing DSO by 1 day frees this much cash.
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                        {(dso.recommendations || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>AI Recommendations</Typography>
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                    {dso.recommendations.map((r, i) => (
                                        <Typography key={i} variant="body2">• {r}</Typography>
                                    ))}
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 2:
                return dpo ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="DPO Optimization Advisor" subtitle="Balance vendor relationships with payment timing" />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                    <Typography variant="caption" fontWeight={600}>Current DPO</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <RadialGauge
                                            value={dpo.current_dpo || 0}
                                            max={90}
                                            label={dpo.current_dpo < 20 ? 'Too Fast' : dpo.current_dpo > 60 ? 'Strained' : 'Optimal'}
                                            color={dpoColor(dpo.current_dpo || 0)}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" fontWeight={600}>Recommendations</Typography>
                                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                        {(dpo.recommendations || []).map((r, i) => (
                                            <Typography key={i} variant="body2">• {r}</Typography>
                                        ))}
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 3:
                return inv ? (
                    <Stack spacing={1}>
                        <SectionHeader title="Inventory Turns Analyzer" subtitle="Optimize inventory investment via turnover analysis" />
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Chip size="small" color={inv.configured ? 'success' : 'default'}
                                label={inv.configured ? 'Configured' : 'Not configured'} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{inv.note}</Typography>
                        </Paper>
                    </Stack>
                ) : <LoadingBlock rows={2} />;
            case 4:
                return forecast && !forecast.error ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Working Capital Forecast" subtitle="Project working capital needs 90 days out" />
                        <SummaryGrid cards={[
                            { label: 'AR @ Horizon', value: fmtMoney(forecast.projected_ar_at_horizon), accent: 'info' },
                            { label: 'AP @ Horizon', value: fmtMoney(forecast.projected_ap_at_horizon), accent: 'warning' },
                            { label: 'WC Delta', value: fmtMoney(forecast.projected_working_capital_delta), accent: forecast.projected_working_capital_delta >= 0 ? 'success' : 'danger' },
                            { label: 'Stress Indicator', value: forecast.stress_indicator || '—', accent: forecast.stress_indicator === 'HEALTHY' ? 'success' : 'warning' },
                        ]} />
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 5:
                return exec ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Executive Briefing"
                            subtitle="AI-generated monthly working capital narrative"
                            actions={
                                <Stack direction="row" spacing={1}>
                                    <FormControlLabel
                                        control={<Switch size="small" checked={useAi} onChange={e => setUseAi(e.target.checked)} />}
                                        label={<Typography variant="caption">AI</Typography>}
                                    />
                                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
                                        onClick={async () => { setExec(null); const r = await run(() => wcExecutive(useAi)); if (r?.data?.success) setExec(r.data.data); }}>
                                        Refresh
                                    </Button>
                                </Stack>
                            }
                        />
                        <AIAnalysisBox text={exec.ai_executive_narrative} title="AI Executive Briefing" />
                        <SummaryGrid cards={[
                            { label: 'DSO', value: `${exec.metrics?.dso_days ?? '—'}d`, sublabel: 'Benchmark: 40-60', accent: 'info' },
                            { label: 'DPO', value: `${exec.metrics?.dpo_days ?? '—'}d`, sublabel: 'Benchmark: 30-45', accent: 'success' },
                            { label: 'CCC', value: `${exec.metrics?.ccc_days ?? '—'}d`, accent: 'primary' },
                        ]} cols={3} />
                        {exec.dso_actions?.recommendations?.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>DSO Actions</Typography>
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                    {exec.dso_actions.recommendations.map((r, i) => (
                                        <Typography key={i} variant="body2">• {r}</Typography>
                                    ))}
                                </Stack>
                            </Paper>
                        )}
                        {exec.narrative && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Basic Summary</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>{exec.narrative}</Typography>
                            </Paper>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Working Capital Optimization Agent"
            subtitle="Agentic Financial Operations — CCC, DSO/DPO, inventory turns, forecast, AI executive briefing with benchmarks."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
